import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { adminGetUsers, adminCreateUser, adminUpdateUser, adminDeleteUser, adminGetStats } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Users, 
  UserPlus, 
  Mail, 
  Building2, 
  Lock,
  Star,
  ToggleLeft,
  ToggleRight,
  Trash2,
  Copy,
  Eye,
  EyeOff,
  Shield
} from 'lucide-react';

export default function AdminDashboard() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});
  
  // Form state
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    business_name: ''
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersData, statsData] = await Promise.all([
        adminGetUsers(),
        adminGetStats()
      ]);
      setUsers(usersData);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load admin data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUser.email || !newUser.password || !newUser.business_name) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setCreating(true);
    try {
      await adminCreateUser(newUser);
      toast.success('Client account created successfully');
      setNewUser({ email: '', password: '', business_name: '' });
      setShowCreateForm(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      await adminUpdateUser(userId, { is_active: !currentStatus });
      toast.success(`Account ${!currentStatus ? 'enabled' : 'disabled'}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleToggleAdmin = async (userId, currentStatus, email) => {
    const action = currentStatus ? 'remove admin rights from' : 'make admin';
    if (!window.confirm(`Are you sure you want to ${action} ${email}?`)) {
      return;
    }
    
    try {
      await adminUpdateUser(userId, { is_admin: !currentStatus });
      toast.success(`${!currentStatus ? 'Admin rights granted' : 'Admin rights removed'}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`Are you sure you want to delete ${email}? This will remove all their data.`)) {
      return;
    }
    
    try {
      await adminDeleteUser(userId);
      toast.success('User deleted successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const copyWebhookUrl = (userId, platform) => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    const url = `${baseUrl}/api/webhook/${platform}/${userId}`;
    navigator.clipboard.writeText(url);
    toast.success(`${platform} webhook URL copied!`);
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewUser({ ...newUser, password });
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-muted rounded w-48" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-32 bg-muted rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-primary" />
            <h1 className="text-2xl lg:text-3xl font-bold text-foreground tracking-tight">
              Admin Dashboard
            </h1>
          </div>
          <p className="text-muted-foreground mt-1">
            Manage client accounts and monitor platform usage
          </p>
        </div>
        <Button 
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="btn-animate"
          data-testid="create-user-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          New Client
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="stat-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="stat-value">{stats?.total_users || 0}</p>
                <p className="stat-label">Total Clients</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="stat-value">{stats?.active_users || 0}</p>
                <p className="stat-label">Active Clients</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <ToggleRight className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="stat-value">{stats?.total_emails_sent || 0}</p>
                <p className="stat-label">Emails Sent</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Mail className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="stat-value">{stats?.total_reviews || 0}</p>
                <p className="stat-label">Total Reviews</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-yellow-100 flex items-center justify-center">
                <Star className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create User Form */}
      {showCreateForm && (
        <Card className="animate-fade-in" data-testid="create-user-form">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-primary" />
              Create New Client Account
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="business_name">Business Name</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="business_name"
                      value={newUser.business_name}
                      onChange={(e) => setNewUser({ ...newUser, business_name: e.target.value })}
                      placeholder="Client's Business"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                      placeholder="client@business.com"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type="text"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      placeholder="Password"
                      className="pl-10 pr-24"
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 text-xs"
                      onClick={generatePassword}
                    >
                      Generate
                    </Button>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={creating}>
                  {creating ? 'Creating...' : 'Create Account'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowCreateForm(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Users List */}
      <Card data-testid="users-list">
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            User Accounts ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {users.length > 0 ? (
            <div className="space-y-4">
              {users.map((u) => (
                <div 
                  key={u.id}
                  className={`flex items-center justify-between p-4 rounded-lg ${u.is_admin ? 'bg-yellow-50 border border-yellow-200' : 'bg-muted/30'}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${u.is_admin ? 'bg-yellow-100' : 'bg-primary/10'}`}>
                      <span className={`text-lg font-semibold ${u.is_admin ? 'text-yellow-600' : 'text-primary'}`}>
                        {u.is_admin ? '★' : u.business_name?.charAt(0)?.toUpperCase() || 'C'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground flex items-center gap-2">
                        {u.business_name}
                        {u.is_admin && (
                          <Badge className="bg-yellow-100 text-yellow-700 text-xs">Admin</Badge>
                        )}
                      </p>
                      <p className="text-sm text-muted-foreground">{u.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">
                          {u.client_count || 0} clients
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {u.reviewed_count || 0} reviews
                        </Badge>
                        <Badge 
                          className={u.is_active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}
                        >
                          {u.is_active !== false ? 'Active' : 'Disabled'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!u.is_admin && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyWebhookUrl(u.id, 'stripe')}
                          title="Copy Stripe Webhook URL"
                        >
                          <Copy className="w-4 h-4 mr-1" />
                          Stripe URL
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyWebhookUrl(u.id, 'shopify')}
                          title="Copy Shopify Webhook URL"
                        >
                          <Copy className="w-4 h-4 mr-1" />
                          Shopify URL
                        </Button>
                      </>
                    )}
                    <Button
                      variant={u.is_admin ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleToggleAdmin(u.id, u.is_admin, u.email)}
                      className={u.is_admin ? 'bg-yellow-500 hover:bg-yellow-600 text-white' : 'border-yellow-500 text-yellow-600 hover:bg-yellow-50'}
                    >
                      <Shield className="w-4 h-4 mr-1" />
                      {u.is_admin ? 'Remove Admin' : 'Make Admin'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleToggleActive(u.id, u.is_active !== false)}
                      title={u.is_active !== false ? 'Disable account' : 'Enable account'}
                    >
                      {u.is_active !== false ? (
                        <ToggleRight className="w-5 h-5 text-emerald-600" />
                      ) : (
                        <ToggleLeft className="w-5 h-5 text-muted-foreground" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteUser(u.id, u.email)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      title="Delete account"
                    >
                      <Trash2 className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No client accounts yet</p>
              <p className="text-sm">Click "New Client" to create your first client account</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
